import { colorMatrix } from "../../../maps/components/MapComponent/constants";
import type { RatesDosageDataInterface } from "../../../pages/new-trail/types";

export const getCircleColor = (
  variety: string | undefined,
  rate: number | undefined,
  trialProducts: any,
  trialMeasureUnit?: any,
  ratesDosageList?: any
): string => {
  if (!trialProducts || !variety || !rate) {
    return "";
  }

  let idx = 0;
  if (trialProducts.length > 1) {
    idx = trialProducts.map((x: any) => x.variety).indexOf(variety) % 10;
  }

  const variantRates =
    ratesDosageList?.filter((item: any) => item.variety === variety) ?? [];

  if (variantRates.length === 0) {
    return "";
  }

  if (variantRates.length === 1) {
    const color = colorMatrix[idx]?.[50] ?? "";
    return color;
  }

  const rates = variantRates.map((item: any) => item.rate);

  const finalColor = getColorBasedOnRate(rate, rates, colorMatrix[idx], idx);

  return finalColor;
};

export function getCircleColorNutrient(
  data: RatesDosageDataInterface[],
  rate: number,
  key?: keyof RatesDosageDataInterface,
  plotIndex?: number
): string {
  if (!rate) {
    return "";
  }

  let valueKey: keyof RatesDosageDataInterface = "fertiliser_rate";
  if (key) {
    valueKey = key;
  }

  const rates = data.map((item) => (item[valueKey] as number) ?? 0);
  const blueColorShade = colorMatrix[plotIndex ?? 0] ?? {};

  const finalColor = getColorBasedOnRate(
    rate,
    rates,
    blueColorShade,
    plotIndex ?? 0
  );

  return finalColor;
}

// export function getCircleColorBiologicals(data: any): string {
//   let color = "";
//   if (data?.treated) {
//     color = colorMatrix[0][50] ?? "#DFE2E7";
//   }
//   return color;
// }

// export function getCircleColorBiologicals(
//   treatment: { treated?: boolean } | undefined
// ): string {
//   const color = treatment?.treated ? colorMatrix[0][50] : "#DFE2E7";
//   return color;
// }

// Persistent global cache and index
const bioColorCache: Record<any, any> = {};
let bioColorIdx = 0;
// CWTRC-2188: navy (hue 210) sits last so it is no longer the first color
// handed out. It used to land on plots marked treated with no treatment
// selected, which read to users as a meaningful state of its own.
const bioHues = [140, 265, 175, 20, 340, 50, 190, 300, 80, 210];
const bioBaseLightness = 20;
const bioBaseSaturation = 90;

// Sonar typescript:S7760 ("prefer default parameters over reassignment") does
// not apply cleanly here: real callers (see PlotTable.tsx, addRatesDosagesToPlotProperties.ts)
// pass an explicit `null` for treatmentID when no treatment is selected, and a
// JS default parameter (`treatmentID: number = 0`) only fires on `undefined`,
// never on `null` — rewriting the signature that way would let an explicit
// `null` fall through unconverted and re-claim a palette color, reintroducing
// the CWTRC-2188 bug. The explicit guard below handles null, undefined, and 0
// alike, so it is kept as-is instead of a default parameter.
export function getCircleColorBiologicals(
  treatment: { treated?: boolean } | undefined,
  treatmentID: number | null
): string {
  if (!treatment?.treated) return "#DFE2E7";

  // A plot is only colored once a treatment is actually selected. No id, or
  // an id of 0, means no selection and must not be mistaken for a real
  // treatment or it claims a palette color of its own.
  if (treatmentID === null || treatmentID === undefined || treatmentID === 0) {
    return "#DFE2E7";
  }

  const treatmentIDNumber: number = treatmentID;

  if (Object.hasOwn(bioColorCache, treatmentIDNumber)) {
    return bioColorCache[treatmentIDNumber];
  }

  // If not cached, assign a new color from hues, cycle through hues if out of range
  const hue = bioHues[bioColorIdx % bioHues.length];
  const color = hslToHex(hue, bioBaseSaturation, bioBaseLightness);
  bioColorCache[treatmentIDNumber] = color;
  bioColorIdx++;
  return color;
}

// Helper: HSL to HEX
function hslToHex(hue: number, saturation: number, lightness: number): string {
  // Clamp ranges
  hue = Math.max(0, Math.min(360, hue));
  saturation = Math.max(0, Math.min(100, saturation)) / 100;
  lightness = Math.max(0, Math.min(100, lightness)) / 100;
  const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = lightness - c / 2;
  let red = 0;
  let green = 0;
  let blue = 0;

  if (hue < 60) {
    red = c;
    green = x;
  } else if (hue < 120) {
    red = x;
    green = c;
  } else if (hue < 180) {
    green = c;
    blue = x;
  } else if (hue < 240) {
    green = x;
    blue = c;
  } else if (hue < 300) {
    red = x;
    blue = c;
  } else {
    red = c;
    blue = x;
  }

  const rgb = [red, green, blue].map((v) => Math.round((v + m) * 255));
  return "#" + rgb.map((n) => n.toString(16).padStart(2, "0")).join("");
}

/**
 * Returns a dynamically calculated color for a rate.
 * Higher rates = darker colors = lower lightness (L).
 * Lower rates = lighter colors = higher lightness (L).
 */
function getColorBasedOnRate(
  rate: number,
  rates: number[],
  colorShade: any,
  idx: number,
  options?: { hue?: number; saturation?: number }
): string {
  if (!rates || rates.length === 0 || typeof rate !== "number") {
    // fallback to static color if needed
    return colorShade?.[50] ?? "";
  }

  // Find min/max for scaling, include `rate` to avoid edge-case mismatch
  const minRate = Math.min(...rates, rate);
  const maxRate = Math.max(...rates, rate);

  // Reverse: higher rate → darker (lower L)
  let lightness = 90;
  if (maxRate !== minRate) {
    lightness = 90 - ((rate - minRate) * (90 - 20)) / (maxRate - minRate); // 90 (light) to 20 (dark)
    lightness = Math.max(20, Math.min(90, lightness));
  }

  // Optionally vary hue per idx, or use options.hue, or fallback to blue (210)
  const hue =
    options?.hue ?? [210, 140, 265, 175, 20, 340, 50, 190, 300, 80][idx % 10];
  const saturation = options?.saturation ?? 60;

  return hslToHex(hue, saturation, lightness);
}
